import React, { useEffect } from 'react'
import { cx } from 'class-variance-authority'
import { Tooltip } from '@material-tailwind/react/components/Tooltip'
import { CheckIcon } from '@heroicons/react/24/solid'
import { usePostHog } from 'posthog-js/react'
import { useApplyStore } from '../../stores/ui/applyStore'
import { NewInput } from '../ui/form/inputs/NewInput'
import { useApplyFormStore } from '../../stores/ui/applyFormStore'
import { Inputfile } from '../ui/form/inputs/Inputfile'
import { TextArea } from '../ui/form/inputs/Textarea'
import { OfficeSelector } from './OfficeSelector'
import { buttonClassName } from '../ui/button/utils'
import { CheckboxInput } from '../ui/form/inputs/Checkbox'
import { useOfferApplyRequest } from './requests/postOfferApplication'
import { ContractTypeSelector } from './ContractTypeSelector'
import { StartDateInput } from './StartDateInput'
import type { Office } from '../firms/firm/mapper'
import { BaseLoader } from '../base/BaseLoader'

export const ApplicationForm = ({
  backendUrl,
  frontStrapiToken,
}: {
  backendUrl: string
  frontStrapiToken: string
}) => {
  // const [isResultValid, setIsResultValid] = useState<boolean>(false);
  // const firstNumber = getRandomInt()
  // const secondNumber = getRandomInt()
  const {
    isCareerSiteApply,
    colorCareerSite,
    offerId,
    closeApplicationFormModal,
    offices,
  } = useApplyStore()

  const {
    setFieldValue,
    cv,
    firstName,
    lastName,
    email,
    message,
    agreed,
    errors,
    validateForm,
    isDirty,
    contractType,
    jobType,
    office,
    startDate,
    resetStateButkeepPersonalInfo,
  } = useApplyFormStore()

  const isSpontaneous = Boolean(!offerId && offices && offices?.length > 0)

  useEffect(() => {
    if (isDirty) {
      validateForm(isSpontaneous)
    }
  }, [
    firstName,
    lastName,
    email,
    agreed,
    cv,
    message,
    contractType,
    jobType,
    office,
    startDate,
  ])

  useEffect(() => {
    setFieldValue('isDirty', false)
  }, [])

  const {
    postOfferApplication,
    postSpontaneousApplication,
    loading,
    success,
    setSuccess,
    succesMessage = 'Votre candidature a bien été envoyée',
  } = useOfferApplyRequest({
    backendUrl,
    frontStrapiToken,
  })

  const postHog = usePostHog()

  const isValid = Object.keys(errors).length === 0 && isDirty
  const isCareerSite = Boolean(isCareerSiteApply && colorCareerSite)

  return (
    <div className="pt-2 w-[95vw] max-w-[700px] my-8 md:my-0">
      <div className="md:px-4 sm:px-0">
        <h2
          className="font-semibold text-night text-base/7"
          style={{ color: (isCareerSite && colorCareerSite) || undefined }}
        >
          Candidature&nbsp;{isSpontaneous && 'Spontanée'}
        </h2>
      </div>

      <form className="relative flex flex-col justify-between mt-6">
        <div
          className={cx(
            'absolute top-0 left-0 right-0 bottom-0 z-10 bg-white flex',
            !success && 'hidden',
          )}
        >
          <div className="flex flex-col items-center justify-center flex-1 space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center justify-center p-3 mx-auto bg-green-100 rounded-full">
                <CheckIcon
                  aria-hidden="true"
                  className="w-8 h-8 text-green-600"
                />
              </div>
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-base font-semibold text-night">
                {succesMessage || 'Votre candidature a bien été envoyée'}
              </h3>
            </div>

            <button
              type="button"
              className={buttonClassName({
                color: isCareerSite ? colorCareerSite || 'apply' : 'apply',
                invert: !isCareerSite,
                disabled: loading,
                size: 'medium',
              })}
              style={{
                backgroundColor: (isCareerSite && colorCareerSite) || undefined,
                color: (isCareerSite && 'white') || undefined,
                borderColor: (isCareerSite && colorCareerSite) || undefined,
              }}
              onClick={() => {
                resetStateButkeepPersonalInfo()
                closeApplicationFormModal()
                setSuccess(false)
              }}
            >
              Fermer
            </button>
          </div>
        </div>

        <div className="flex-1">
          <div className="grid grid-cols-1 gap-x-2 gap-y-6 sm:grid-cols-2">
            <NewInput
              value={firstName}
              name="firstName"
              label="Prénom"
              placeholder="Prénom"
              required
              autoComplete="given-name"
              onChange={(value) => setFieldValue('firstName', value)}
              error={errors?.firstName}
            />
            <NewInput
              value={lastName}
              name="lastName"
              label="Nom"
              placeholder="Nom"
              autoComplete="family-name"
              onChange={(value) => setFieldValue('lastName', value)}
              error={errors?.lastName}
            />

            <div className="md:col-span-2">
              <NewInput
                value={email}
                name="email"
                label="Email"
                placeholder="Mika@exemple.com"
                autoComplete="email"
                type="email"
                onChange={(value) => setFieldValue('email', value)}
                error={errors?.email}
              />
            </div>
            <div className="md:col-span-2">
              <Inputfile
                value={cv as File | null}
                name="cv"
                label="CV"
                onChange={(value) => setFieldValue('cv', value)}
                styleType="small"
                error={errors?.cv}
              />
            </div>

            <div
              className={cx(
                'grid grid-cols-1 md:col-span-2 gap-x-2 gap-y-6',
                !isSpontaneous && 'sr-only',
              )}
            >
              <OfficeSelector
                offices={(offices as NonNullable<Office>[]) || []}
                name="office"
                error={errors?.office}
              />
              <NewInput
                value={jobType || undefined}
                name="jobType"
                label="Quel type de poste vous intéresse?"
                onChange={(value) => setFieldValue('jobType', value)}
                error={errors?.jobType}
              />
              <ContractTypeSelector
                name="contractType"
                error={errors?.contractType}
              />
              <StartDateInput
                name="contractType"
                error={errors?.contractType}
              />
            </div>

            <div className="md:col-span-2">
              <TextArea
                value={message}
                name="message"
                placeholder="Ecris ton message pour le cabinet ici."
                label="Message"
                onChange={(value) => setFieldValue('message', value)}
                error={errors?.message}
              />
            </div>
            <div className="md:col-span-2">
              <CheckboxInput
                checked={agreed}
                onChange={(checked) => setFieldValue('agreed', checked)}
                label="Je souhaite recevoir des offres équivalentes"
                name="agreed"
              />
            </div>
            {/* <div className='md:col-span-2'>
                  {!isResultValid && <RandomCalculation
                    setIsResultValid={setIsResultValid}
                    firstNumber={firstNumber}
                    secondNumber={secondNumber}
                  />}
                </div> */}
          </div>

          <div className="flex items-center justify-center px-4 py-2 mt-4 border-t md:justify-end gap-x-6 border-gray-900/10 sm:px-8">
            <button
              type="button"
              className="font-semibold text-night text-sm/6"
              title="Annuler"
              onClick={() => {
                resetStateButkeepPersonalInfo()
                closeApplicationFormModal()
                postHog?.capture('application_form_canceled', {
                  firstName,
                  lastName,
                  email,
                  office,
                  offerId,
                })
              }}
            >
              Annuler
            </button>
            <Tooltip
              className="px-4 py-3 border shadow-xl border-lamacompta-blue bg-very-light-blue shadow-black/10"
              content={
                <div className="space-y-1">
                  <p className="text-sm text-center text-night">
                    {!isValid ? 'Veuillez corriger les erreurs' : 'Candidater'}
                  </p>
                </div>
              }
            >
              <button
                type="button"
                className={buttonClassName({
                  color: isCareerSite ? colorCareerSite || 'apply' : 'apply',
                  invert: !isCareerSite,
                  disabled: !isValid || loading,
                  size: 'medium',
                })}
                title={
                  !isValid ? 'Veuillez compléter le formulaire' : 'Candidater'
                }
                disabled={!isValid || loading}
                style={{
                  backgroundColor:
                    (isCareerSite && colorCareerSite) || undefined,
                  color: (isCareerSite && 'white') || undefined,
                  borderColor: (isCareerSite && colorCareerSite) || undefined,
                }}
                onClick={async (e) => {
                  e.preventDefault()

                  if (isSpontaneous) {
                    if (typeof window !== 'undefined' && window?.gtag) {
                      window.gtag('event', 'spontaneous application', {
                        event_category: 'conversion',
                        event_label: `Candidature spontanée au cabinet ${window?.location?.pathname}`,
                        company_name: offices?.find((o) => o?.uuid === office)
                          ?.name,
                        event_firstName: firstName,
                        event_lastName: lastName,
                        event_email: email,
                        officeId: office,
                      })
                    }

                    postHog?.capture('spontaneous_application_submitted', {
                      firstName,
                      lastName,
                      email,
                      office,
                    })

                    await postSpontaneousApplication({
                      firstName,
                      lastName,
                      email,
                      message,
                      cv: cv as File,
                      agreed,
                      contractType: contractType as string,
                      jobType: jobType as string,
                      startDate: startDate as string,
                      office: office as string,
                    })
                  } else {
                    if (typeof window !== 'undefined' && window?.gtag) {
                      window.gtag('event', 'offer application', {
                        event_category: 'conversion',
                        event_label: `Candidature soumise à l'offre ${window?.location?.pathname}`,
                        event_firstName: firstName,
                        event_lastName: lastName,
                        event_email: email,
                        offer_id: offerId,
                      })
                    }

                    postHog?.capture('offer_application_submitted', {
                      firstName,
                      lastName,
                      email,
                      offerId,
                    })

                    await postOfferApplication({
                      firstName,
                      lastName,
                      email,
                      message,
                      cv: cv as File,
                      agreed,
                      offerId: offerId as string,
                    })
                  }
                }}
              >
                {!loading ? 'Candidater' : <BaseLoader />}
              </button>
            </Tooltip>
          </div>
        </div>
      </form>
    </div>
  )
}
